import React, { useState } from "react";
import {
    IResourceComponentsProps,
    useNavigation,
} from "@pankod/refine-core";
import {
    List,
    Stack,
    Typography,
    Grid,
    Card,
    Button,
    useAutocomplete,
    Autocomplete,
    TextField,
    Box,
    Paper,
    Avatar,
} from "@pankod/refine-mui";
import { Print, Visibility } from "@mui/icons-material";
import { IStudent } from "../../interfaces";

export const LibraryReportList: React.FC<IResourceComponentsProps> = () => {

    const [selectedStudent, setSelectedStudent] = useState<IStudent | null>(null);
    const { show } = useNavigation();

    const { autocompleteProps } = useAutocomplete<IStudent>({
        resource: "student",
    });

    const handleGenerateReport = () => {
        if (selectedStudent) {
            const token = localStorage.getItem("token");
            const reportUrl = `${process.env.REACT_APP_BACKEND}admin/libraryReport/${selectedStudent.id}/pdf`;

            fetch(reportUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text();
                })
                .then(htmlContent => {
                    // Open HTML content in new window (no auto-print)
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                        printWindow.document.write(htmlContent);
                        printWindow.document.close();
                    } else {
                        console.error('Failed to open print window - popup blocked?');
                        alert('Failed to open report. Please allow popups for this site.');
                    }
                })
                .catch(error => {
                    console.error('Error generating PDF:', error);
                    alert('Failed to generate report. Please try again.');
                });
        }
    };



    return (
        <List
            title="Library Reading Reports"
            headerButtons={
                <Box>
                    <Typography variant="body2" color="textSecondary">
                        Generate Library reading progress reports
                    </Typography>
                </Box>
            }
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Student Reading Report
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Select a student to generate reading progress report.
                        </Typography>

                        <Stack spacing={3}>
                            <Autocomplete
                                {...autocompleteProps}
                                value={selectedStudent}
                                onChange={(_, value) => setSelectedStudent(value)}
                                getOptionLabel={(item) => `${item.firstName} ${item.lastName} (Grade ${item.grade})`}
                                isOptionEqualToValue={(option, value) =>
                                    value === undefined || option.id === value.id
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Student"
                                        placeholder="Search by student name..."
                                        variant="outlined"
                                        fullWidth
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                            {option.firstName?.[0]}{option.lastName?.[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body1">
                                                {option.firstName} {option.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Grade {option.grade} • {option.school?.name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />

                            {selectedStudent && (
                                <Card sx={{ p: 2, bgcolor: 'primary.50' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ width: 48, height: 48 }}>
                                            {selectedStudent.firstName?.[0]}{selectedStudent.lastName?.[0]}
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="h6">
                                                {selectedStudent.firstName} {selectedStudent.lastName}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Grade {selectedStudent.grade} • {selectedStudent.school?.name}
                                            </Typography>
                                            {selectedStudent.teacher && (
                                                <Typography variant="caption" color="textSecondary">
                                                    Teacher: {selectedStudent.teacher.firstName} {selectedStudent.teacher.lastName}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Card>
                            )}

                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    startIcon={<Print />}
                                    onClick={handleGenerateReport}
                                    disabled={!selectedStudent}
                                    size="large"
                                >
                                    Generate PDF Report
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => selectedStudent && show("libraryReport", selectedStudent.id)}
                                    disabled={!selectedStudent}
                                    size="large"
                                >
                                    View Details
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

            </Grid>
        </List>
    );
};